import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../utils/format";

type ReversePaymentModalProps = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  itemId: number;
  itemDescription: string;
  itemType: "payable" | "receivable";
};

export const ReversePaymentModal: React.FC<ReversePaymentModalProps> = ({
  show,
  onClose,
  onSuccess,
  itemId,
  itemDescription,
  itemType,
}) => {
  const { client } = useAuth();
  const [reverseDate, setReverseDate] = useState("");
  const [reverseReason, setReverseReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreditCard, setIsCreditCard] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<{
    paymentDate?: string | null;
    amount?: number | null;
    totalAmount?: number | null;
    paymentMethodName?: string | null;
    creditCardName?: string | null;
    creditCardInvoice?: { competenceMonth: number; competenceYear: number } | null;
    creditCardTransactionId?: number | null;
  } | null>(null);

  useEffect(() => {
    if (!show) {
      return;
    }
    setReverseDate(new Date().toISOString().split("T")[0]);
    setReverseReason("");
    setError(null);
    setIsCreditCard(false);
    setPaymentInfo(null);

    if (itemType === "payable") {
      const loadLastPayment = async () => {
        const response = await client.get(
          `/modules/payments/payables/${itemId}/payments`
        );
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as Array<{
          paymentDate?: string;
          amount?: number;
          totalAmount?: number | null;
          paymentMethod?: { type?: string | null; name?: string | null };
          creditCard?: { name?: string | null } | null;
          creditCardInvoice?: { competenceMonth: number; competenceYear: number } | null;
        }>;
        const methodType = payload[0]?.paymentMethod?.type ?? null;
        setIsCreditCard(methodType === "CARTAO_CREDITO");
        setPaymentInfo({
          paymentDate: payload[0]?.paymentDate ?? null,
          amount: payload[0]?.amount ?? null,
          totalAmount: payload[0]?.totalAmount ?? null,
          paymentMethodName: payload[0]?.paymentMethod?.name ?? null,
          creditCardName: payload[0]?.creditCard?.name ?? null,
          creditCardInvoice: payload[0]?.creditCardInvoice ?? null,
        });
      };
      void loadLastPayment();

      const loadPayableDetails = async () => {
        const response = await client.get(`/modules/payables/${itemId}`);
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as {
          data?: { creditCardTransactionId?: number | null };
        };
        if (payload.data) {
          setPaymentInfo((prev) => ({
            ...(prev ?? {}),
            creditCardTransactionId: payload.data?.creditCardTransactionId ?? null,
          }));
        }
      };
      void loadPayableDetails();
    }
  }, [show, client, itemId, itemType]);

  const title = itemType === "receivable" ? "Estornar recebimento" : "Estornar pagamento";
  const endpoint =
    itemType === "receivable"
      ? `/modules/payments/receivables/${itemId}/reverse`
      : `/modules/payments/payables/${itemId}/reverse`;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!reverseReason.trim()) {
        setError("Informe o motivo do estorno.");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await client.post(endpoint, {
          reverseDate,
          reverseReason: reverseReason.trim(),
        });

        if (!response.ok) {
          const errorPayload = await response.json().catch(() => ({}));
          throw new Error(errorPayload?.error || "Falha ao estornar pagamento.");
        }

        onSuccess();
        onClose();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Falha ao estornar pagamento.";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [client, endpoint, reverseDate, reverseReason, onClose, onSuccess]
  );

  if (!show) {
    return null;
  }

  const paymentDateLabel = paymentInfo?.paymentDate
    ? formatDateBR(paymentInfo.paymentDate)
    : "-";
  const paymentAmountValue =
    paymentInfo?.totalAmount ?? paymentInfo?.amount ?? null;
  const paymentAmountLabel =
    paymentAmountValue === null || paymentAmountValue === undefined
      ? "-"
      : formatMoneyBR(paymentAmountValue);
  const paymentMethodLabel = paymentInfo?.paymentMethodName ?? "-";
  const creditCardLabel = paymentInfo?.creditCardName ?? "-";
  const invoiceLabel =
    paymentInfo?.creditCardInvoice
      ? `${String(paymentInfo.creditCardInvoice.competenceMonth).padStart(2, "0")}/${paymentInfo.creditCardInvoice.competenceYear}`
      : "-";
  const transactionLabel =
    paymentInfo?.creditCardTransactionId ??
    paymentInfo?.creditCardTransactionId === 0
      ? String(paymentInfo.creditCardTransactionId)
      : "-";
  const missingTransactionLink =
    isCreditCard && (!paymentInfo?.creditCardTransactionId || paymentInfo.creditCardTransactionId <= 0);

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Descricao</label>
                  <p className="text-muted mb-0">{itemDescription}</p>
                </div>

                <div className="border rounded p-3 mb-3">
                  <div className="fw-semibold mb-2">Referencia do pagamento</div>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-muted small">Data do pagamento</div>
                      <div className="fw-semibold">{paymentDateLabel}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Valor pago</div>
                      <div className="fw-semibold">{paymentAmountLabel}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small">Forma de pagamento</div>
                      <div className="fw-semibold">{paymentMethodLabel}</div>
                    </div>
                    {isCreditCard && (
                      <>
                        <div className="col-6">
                          <div className="text-muted small">Cartao</div>
                          <div className="fw-semibold">{creditCardLabel}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-muted small">Fatura</div>
                          <div className="fw-semibold">{invoiceLabel}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-muted small">Transacao do cartao</div>
                          <div className="fw-semibold">{transactionLabel}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {isCreditCard && (
                  <div className="alert alert-info">
                    Estorno ira remover o lancamento do cartao e ajustar a fatura.
                  </div>
                )}
                {missingTransactionLink && (
                  <div className="alert alert-warning">
                    Pagamento via cartao sem vinculo de transacao, necessario ajustar ou rodar reconciliacao.
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Data do estorno *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={reverseDate}
                    onChange={(event) => setReverseDate(event.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Motivo *</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={reverseReason}
                    onChange={(event) => setReverseReason(event.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-warning"
                  disabled={loading || missingTransactionLink}
                >
                  {loading ? "Processando..." : "Confirmar estorno"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
    </>
  );
};
